/**
 * 산출기초 사칙연산 · 천원(정수) 표시
 * - 허용: 숫자, + - * / ( ) . , 공백
 * - 한글 단위(원·개월·명·회 등)는 제거하고 숫자·연산자만 평가
 * - 기본(「천원」미표기): 원 단위로 보고 /1000 → 천원
 *   · 지출: 절상(ceil) · 수입: 절사(floor)
 * - 「천원」표기: 이미 천원 → 정수 반올림만
 * - 콤마는 무시
 */

/** 산출기초에서 제거하는 한글·영문 단위 */
const UNIT_STRIP =
  /천원|원|개월|월|명|회|일|년|개|식|건|대|인|분|시간|주|분기|반기|명분|인분|EA|ea|원정/g;

/**
 * 원→천원 환산 반올림
 * @param {number} wonValue 원 단위 연산 결과
 * @param {'지출'|'수입'|string} inoutDiv
 */
export function wonToThousand(wonValue, inoutDiv = '지출') {
  const q = Number(wonValue) / 1000;
  if (!Number.isFinite(q)) return null;
  // 지출 절상 · 수입 절사
  if (inoutDiv === '수입') return Math.floor(q);
  return Math.ceil(q);
}

/**
 * @param {string|number|null|undefined} expr
 * @param {{ inoutDiv?: string }} [opts]
 * @returns {number|null} 천원 정수
 */
export function evalArithmetic(expr, opts = {}) {
  if (expr == null) return null;
  const raw = String(expr).trim();
  if (!raw) return null;

  const inoutDiv = opts.inoutDiv || '지출';
  // 「천원」만 명시하면 이미 천원. 그 외(원 표기 유무 무관)는 원 단위
  const hasCheonWon = /천원/.test(raw);

  const s = raw
    .replace(UNIT_STRIP, '')
    .replace(/,/g, '')
    .replace(/\s+/g, '');
  if (!s) return null;
  if (!/^[-+*/().0-9]+$/.test(s)) return null;

  try {
    // eslint-disable-next-line no-new-func
    const v = Function(`"use strict"; return (${s});`)();
    if (typeof v !== 'number' || !Number.isFinite(v)) return null;
    if (hasCheonWon) return Math.round(v);
    return wonToThousand(v, inoutDiv);
  } catch {
    return null;
  }
}

/** 천원 정수 → 콤마 표시 */
export function formatThousand(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '';
  return Math.round(n).toLocaleString('ko-KR');
}

/**
 * 산출기초 식의 숫자에 천단위 콤마 삽입 (연산자·단위 유지)
 * - 기존 콤마를 먼저 제거한 뒤 다시 찍어 `3,5,000` 같은 깨짐을 방지
 * 예: `5000000원 * 12개월` → `5,000,000원 * 12개월`
 */
export function formatCalcBasisCommas(expr) {
  if (expr == null || expr === '') return '';
  const stripped = String(expr).replace(/,/g, '');
  return stripped.replace(/\d+(?:\.\d+)?/g, (num) => {
    const [intPart, decPart] = num.split('.');
    const withComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart != null ? `${withComma}.${decPart}` : withComma;
  });
}

/**
 * 입력 중 콤마 삽입 + 캐럿 위치 보정 (콤마 제외 raw 길이 기준)
 * @returns {{ value: string, caret: number }}
 */
export function formatCalcBasisCommasWithCaret(expr, caret = 0) {
  const raw = expr == null ? '' : String(expr);
  const safeCaret = Math.max(0, Math.min(Number(caret) || 0, raw.length));
  const rawBeforeLen = raw.slice(0, safeCaret).replace(/,/g, '').length;
  const value = formatCalcBasisCommas(raw);
  let pos = 0;
  let rawCount = 0;
  while (pos < value.length && rawCount < rawBeforeLen) {
    if (value[pos] !== ',') rawCount += 1;
    pos += 1;
  }
  return { value, caret: pos };
}

/** 입력 문자열 → 천원 정수 (콤마 제거) */
export function parseThousand(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

/**
 * 산출 디테일 롤업
 * - 리프: 산출기초 사칙연산 → 산출금액(천원)
 * - 부모(직계 하위 레벨이 있는 행): 직계 자식 산출금액 합
 * @param {Array} rows
 * @param {{ inoutDiv?: string }} [opts] 지출=원→천원 절상, 수입=절사
 * @returns {Array}
 */
export function rollupCalcDetails(rows = [], opts = {}) {
  const list = (rows || []).map((r) => ({ ...r }));
  const activeIdx = [];
  list.forEach((r, i) => {
    if (r.mod !== 'D') activeIdx.push(i);
  });

  const levelOf = (i) => Math.min(5, Math.max(1, Number(list[i]?.calcLevel) || 1));

  for (let a = activeIdx.length - 1; a >= 0; a -= 1) {
    const i = activeIdx[a];
    const myLv = levelOf(i);
    const childIdx = [];
    for (let b = a + 1; b < activeIdx.length; b += 1) {
      const j = activeIdx[b];
      const lv = levelOf(j);
      if (lv <= myLv) break;
      if (lv === myLv + 1) childIdx.push(j);
    }
    if (childIdx.length) {
      const sum = childIdx.reduce((s, j) => s + (Number(list[j].calcAmt) || 0), 0);
      list[i] = {
        ...list[i],
        calcAmt: sum,
        hasChildren: true,
        calcBasis: '',
      };
    } else {
      const fromFormula = evalArithmetic(list[i].calcBasis, opts);
      if (fromFormula != null) {
        list[i] = { ...list[i], calcAmt: fromFormula, hasChildren: false };
      } else if (list[i].calcAmt === '' || list[i].calcAmt == null) {
        list[i] = { ...list[i], calcAmt: 0, hasChildren: false };
      } else {
        list[i] = { ...list[i], calcAmt: Number(list[i].calcAmt) || 0, hasChildren: false };
      }
    }
  }
  return list;
}

/** 산출기초 편집 가능 여부 — 합산행·수동잠금(Alt+`) 시 false */
export function isCalcBasisEditable(row) {
  if (!row || row.mod === 'D') return false;
  if (row.hasChildren) return false;
  if (row.basisLocked) return false;
  return true;
}

/** 산출금액 편집 가능 — 합산행은 항상 잠금 */
export function isCalcAmtEditable(row) {
  if (!row || row.mod === 'D') return false;
  if (row.hasChildren) return false;
  return true;
}

/** 마스터 예산액 = 최상위 레벨(보통 1) 행들의 산출금액 합 (천원) */
export function sumTopLevelBudgetAmt(details = [], opts = {}) {
  const rolled = rollupCalcDetails(details, opts).filter((r) => r.mod !== 'D');
  if (!rolled.length) return 0;
  const minLv = Math.min(...rolled.map((r) => Math.min(5, Math.max(1, Number(r.calcLevel) || 1))));
  return rolled
    .filter((r) => Math.min(5, Math.max(1, Number(r.calcLevel) || 1)) === minLv)
    .reduce((s, r) => s + (Number(r.calcAmt) || 0), 0);
}
